import { ErrorLike } from '@apollo/client';
import { useLazyQuery } from '@apollo/client/react';
import * as R from 'ramda';

import { GET_ASSET_USAGE, GET_ASSET_USAGE_SUMMARY } from '@atom/graph/asset';
import { AssetUsage, AssetUsageSummary } from '@atom/types/asset';

type UseAssetUsageOptions = {
  assetId: string;
};

type UseAssetUsageReturn = {
  summary: AssetUsageSummary;
  summaryLoading: boolean;
  summaryCalled: boolean;
  summaryError?: ErrorLike;
  fetchSummary: () => void;
  usage: AssetUsage[];
  totalCount: number;
  usageLoading: boolean;
  usageCalled: boolean;
  usageError?: ErrorLike;
  fetchUsage: (input?: { page?: number; limit?: number }) => void;
};

const useAssetUsage = ({
  assetId,
}: UseAssetUsageOptions): UseAssetUsageReturn => {
  const [
    getSummary,
    {
      data: summaryData,
      loading: summaryLoading,
      called: summaryCalled,
      error: summaryError,
    },
  ] = useLazyQuery(GET_ASSET_USAGE_SUMMARY, { fetchPolicy: 'no-cache' });

  const fetchSummary = () => {
    getSummary({ variables: { assetId } });
  };

  const [
    getUsage,
    {
      data: usageData,
      loading: usageLoading,
      called: usageCalled,
      error: usageError,
    },
  ] = useLazyQuery(GET_ASSET_USAGE, { fetchPolicy: 'no-cache' });

  const fetchUsage = (input: { page?: number; limit?: number } = {}) => {
    getUsage({ variables: { input: { assetId, ...input } } });
  };

  return {
    summary: R.pathOr(null, ['assetUsageSummary'], summaryData),
    summaryLoading,
    summaryCalled,
    summaryError,
    fetchSummary,
    usage: R.pathOr([], ['assetUsage', 'usage'], usageData),
    totalCount: R.pathOr(0, ['assetUsage', 'totalCount'], usageData),
    usageLoading,
    usageCalled,
    usageError,
    fetchUsage,
  };
};

export default useAssetUsage;
